import React from "react";
import { gql } from "apollo-boost";

import SettingsList, { enumOptions, humanize } from "superAdminViews/setting/SettingsList";

const ADD = gql`
  mutation createParcelVolumeByAdmin(
    $name: String!
    $value: Float!
    $order: Int
    $typeOfAttribute: ParcelVolumeTypeOfAttribute!
  ) {
    createParcelVolumeByAdmin(
      input: { name: $name, value: $value, order: $order, typeOfAttribute: $typeOfAttribute }
    ) {
      _id
    }
  }
`;

const GET = gql`
  query getParcelVolumesByAdmin($limit: Int, $skip: Int) {
    getParcelVolumesByAdmin(pagination: { limit: $limit, skip: $skip }) {
      _id
      name
      value
      order
      typeOfAttribute
    }
  }
`;

const COUNT = gql`
  query getParcelVolumesByAdminCount {
    getParcelVolumesByAdminCount
  }
`;

const UPDATE = gql`
  mutation updateParcelVolumeByAdmin(
    $id: ID!
    $name: String!
    $value: Float!
    $order: Int
    $type: ParcelVolumeTypeOfAttribute!
  ) {
    updateParcelVolumeByAdmin(
      id: $id
      input: { name: $name, value: $value, order: $order, typeOfAttribute: $type }
    ) {
      _id
      name
      value
      order
      typeOfAttribute
    }
  }
`;

const DELETE = gql`
  mutation removeParcelVolumeByAdmin($idSet: [ID!]!) {
    removeParcelVolumeByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

const TYPES = ["NUMBER", "PERCENTAGE"];

const FIELDS = [
  {
    name: "name",
    label: "Band",
    type: "text",
    required: true,
    hint: "What the sender sees, for example “Up to 30 x 30 x 30 cm”.",
  },
  {
    name: "typeOfAttribute",
    label: "Charged as",
    type: "select",
    required: true,
    defaultValue: "NUMBER",
    options: enumOptions(TYPES),
    hint: "A flat amount, or a percentage added to the delivery price.",
  },
  { name: "value", label: "Amount", type: "number", step: "0.01", required: true },
  {
    name: "order",
    label: "Position",
    type: "number",
    defaultValue: 0,
    hint: "Lower numbers appear first.",
  },
];

/**
 * ParcelVolume - the size bands a parcel delivery is priced by.
 *
 * Adding and deleting a band lived in a separate 588-line screen that
 * also handled weight; both now go through the same list as everything
 * else, and the two tabs no longer share mutable state.
 */
const ParcelVolume = () => (
  <SettingsList
    title="Parcel volume"
    description="The size bands a parcel delivery is priced by."
    singular="band"
    list={{ query: GET, key: "getParcelVolumesByAdmin" }}
    count={{ query: COUNT, key: "getParcelVolumesByAdminCount" }}
    create={{
      mutation: ADD,
      variables: (values) => ({
        name: values.name.trim(),
        value: parseFloat(values.value),
        order: values.order === "" ? null : parseInt(values.order, 10),
        typeOfAttribute: values.typeOfAttribute,
      }),
    }}
    update={{
      mutation: UPDATE,
      variables: (values, row) => ({
        id: row._id,
        name: values.name.trim(),
        value: parseFloat(values.value),
        order: values.order === "" ? null : parseInt(values.order, 10),
        type: values.typeOfAttribute,
      }),
    }}
    remove={{ mutation: DELETE }}
    columns={[
      { key: "name", label: "Band", width: "2fr" },
      {
        key: "value",
        label: "Amount",
        render: (row) =>
          row.typeOfAttribute === "PERCENTAGE" ? `${row.value}%` : `$${row.value}`,
      },
      {
        key: "typeOfAttribute",
        label: "Charged as",
        render: (row) => humanize(row.typeOfAttribute),
      },
      { key: "order", label: "Position", width: "90px" },
    ]}
    fields={FIELDS}
    toValues={(row) => ({
      name: row.name,
      value: row.value,
      order: row.order === null || row.order === undefined ? "" : row.order,
      typeOfAttribute: row.typeOfAttribute,
    })}
  />
);

export default ParcelVolume;
